/**
 * ClientOptionsDialog — Per-workstation MachineSetting editor (SP13.0).
 *
 * Mirrors the legacy WinForms Client Options form (
 * archive/legacy-requirements/client_system_options/images/client_options_popup.png +
 * detailed_analysis_client_options.md). Two tabs (Main, Tools), four
 * grouped sections on Main (Vault, Cashier, Signature/Document, Hardware),
 * one Comparison Tool section on Tools.
 *
 * Behaviors preserved from legacy:
 *   - Auto-create on first open via GET /current — handler does the work.
 *   - Per-field RBAC disable: each control gated by its `Client Options\Edit:
 *     <Field>` permission via useCanAccess().
 *   - System-flag gating: `useDigitalSignatures` disables DigitalSignatureType;
 *     `allowTipBoxTransactions` disables DefaultTipDropToTipBox.
 *   - Inverse-printing UI checkbox: "Display all documents after transactions"
 *     is `!allowAutomaticPrinting` (inversion handled in commands.ts).
 *   - "Copy client options from" picker: populates form values from another
 *     row without changing the current row's identity.
 *   - Quote-strip on save: ComparisonToolFilePath leading/trailing `"`
 *     stripped server-side.
 *
 * Identifiers (machineName) are read-only display fields per Q1.
 */
import { useCallback, useEffect, useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { declarations } from '@ic/security-registry';
import { useCanAccess } from '@/hooks/useCanAccess';
import { useDropdownLookup } from '@/hooks/useDropdownLookup';
import { useNotifications } from '@/hooks/useNotifications';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { EditPanel } from '@/components/common/EditPanel';
import {
  getCurrentClientOptions,
  listAllClientOptions,
  saveClientOptions,
  type ClientOptionsDialogState,
} from './commands';
import type { ClientOptionsLookupRow, DigitalSignatureType } from './types';

interface ClientOptionsDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const SIGNATURE_TYPE_OPTIONS: Array<{ value: DigitalSignatureType; label: string }> = [
  { value: 'MouseDrawSignaturePanelProvider', label: 'Mouse / Track Pad / Touch Screen' },
  { value: 'TopazSignaturePadProvider', label: 'Signature Pad: Topaz' },
];

const PERMISSION = {
  copy: declarations.ClientOptions.copySecurityOptions.permission,
  cashVault: declarations.ClientOptions.editLockedDownCashVault.permission,
  chipVault: declarations.ClientOptions.editLockedDownChipVault.permission,
  ncvChipVault: declarations.ClientOptions.editLockedDownNCVChipVault.permission,
  allowVault: declarations.ClientOptions.editAllowVaultTransactions.permission,
  showVariance: declarations.ClientOptions.editShowCashierVariance.permission,
  showOtherShifts: declarations.ClientOptions.editShowOtherCashierShifts.permission,
  sigSummary: declarations.ClientOptions.editSignaturePadShowSummaryPage.permission,
  remindRoll: declarations.ClientOptions.editRemindCashiersToRollShift.permission,
  autoForms: declarations.ClientOptions.editAutomaticallyGenerateForms.permission,
  cbValidate: declarations.ClientOptions.editRequireCurrencyBagValidationOnClose.permission,
  sigType: declarations.ClientOptions.editDigitalSignatureType.permission,
  tipBox: declarations.ClientOptions.editDefaultTipTransactionsToTipBox.permission,
  warnOpenVault: declarations.ClientOptions.editWarnIfOpenVault.permission,
  useOriginalVault: declarations.ClientOptions.editUseOriginalVaultWhenApprovingPendingTransactions.permission,
  fundingVault: declarations.ClientOptions.editFundingVault.permission,
  isMobile: declarations.ClientOptions.editIsMobile.permission,
} as const;

export function ClientOptionsDialog({
  open,
  onClose,
  onSaved,
}: ClientOptionsDialogProps) {
  const { addNotification } = useNotifications();
  const { data: lookups } = useDropdownLookup(['vaultTypes']);
  const { canAccess: has } = useCanAccess();

  const [form, setForm] = useState<ClientOptionsDialogState | null>(null);
  const [machineList, setMachineList] = useState<ClientOptionsLookupRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);


  const vaultOptions = lookups?.vaultTypes ?? [];

  // ── Load current row + machine list on open ────────────────────────────
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setForm(null);
    setLoadError(null);
    setSaveError(null);

    (async () => {
      try {
        const current = await getCurrentClientOptions();
        if (cancelled) return;
        setForm(current);
      } catch (err: unknown) {
        if (cancelled) return;
        const apiError = (
          err as { response?: { data?: { error?: { code?: string; message?: string } } } }
        )?.response?.data?.error;
        if (apiError?.code === 'MACHINE_SETTING_AMBIGUOUS_MATCH') {
          setLoadError(
            'Ambiguous workstation match — multiple machine records share this IP. Contact your administrator.',
          );
        } else {
          setLoadError(apiError?.message ?? 'Failed to load Client Options.');
        }
      }
    })();

    if (has(PERMISSION.copy)) {
      listAllClientOptions()
        .then((list) => !cancelled && setMachineList(list))
        .catch(() => undefined);
    }

    return () => {
      cancelled = true;
    };
  }, [open, has]);

  const update = useCallback(<K extends keyof ClientOptionsDialogState>(
    key: K,
    value: ClientOptionsDialogState[K],
  ) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  /**
   * "Copy client options from" picker — overwrite current form values from
   * the source row, but preserve `id`, `machineName` (the current
   * workstation's identity stays put). The legacy desktop also
   * skips printer names, DigitalSignatureType, Description, ComparisonTool*
   * (analysis §2.2 step 1 + §7).
   */
  const handleCopyFrom = useCallback(
    async (_sourceId: number) => {
      if (!form) return;
      try {
        // Re-use the same /current shape by fetching the picker row's full
        // copy via the same handler — but /current only returns the
        // fingerprint-resolved row for the calling machine, not an arbitrary
        // picker row. For SP13.0 we approximate by fetching the lookup row's
        // Id/Description only. To keep scope tight, the "copy" semantic in
        // v1 is implemented server-side or as a future GET /list/:id detail
        // call.
        // For now, surface a note and no-op.
        // TODO(SP13.1) — add GET /list/:id detail to power true copy-from.
        addNotification({
          type: 'info',
          title: 'Not implemented in v1',
          message:
            'Copying values from another workstation requires a follow-up endpoint and ships in a later sprint.',
        });
      } catch {
        // ignore
      }
    },
    [form, addNotification],
  );

  // ── Save ────────────────────────────────────────────────────────────────
  const handleOk = useCallback(async () => {
    if (!form) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await saveClientOptions(form);
      addNotification({
        type: 'success',
        title: 'Client Options saved',
        message: `Settings for ${form.machineName} updated.`,
      });
      onSaved?.();
      onClose();
    } catch (err: unknown) {
      const apiError = (
        err as { response?: { data?: { error?: { code?: string; message?: string } } } }
      )?.response?.data?.error;
      if (apiError?.code === 'CLIENT_OPTIONS_FIELD_FORBIDDEN') {
        setSaveError(apiError.message ?? 'You do not have permission to edit one of the changed fields.');
      } else if (apiError?.code === 'USE_DIGITAL_SIGNATURES_OFF') {
        setSaveError(
          'Digital signatures are disabled site-wide. Re-enable them in System Settings before changing the signature type.',
        );
      } else if (apiError?.code === 'ALLOW_TIP_BOX_TRANSACTIONS_OFF') {
        setSaveError(
          'Tip box transactions are disabled site-wide. Re-enable them in System Settings before toggling this field.',
        );
      } else {
        setSaveError(apiError?.message ?? 'Save failed.');
      }
    } finally {
      setIsSaving(false);
    }
  }, [form, addNotification, onSaved, onClose]);

  // ── Render ──────────────────────────────────────────────────────────────
  // SC-33: previously baked the machine name into `title` itself (`"Client
  // Options - VLT-042"`), which duplicated once EditPanel's header inverted
  // (the whole string as both the small eyebrow and, via fallback, the big
  // bold line). Split so the action label is the eyebrow and the machine
  // name — the actual record identifier — is the prominent line, falling
  // back to the action label alone before a machine has loaded.
  //
  // `ariaLabel` is passed explicitly to preserve the pre-existing accessible
  // name exactly ("Client Options - WS-01") — the combined string is still
  // the right *accessible* name even though it's split into two elements
  // visually now.
  const titleSuffix = form ? ` - ${form.machineName}` : '';

  return (
    // IC-601: ClientOptionsHost mounts this at the AppShell root — a plain
    // `flex-col` container with no horizontal `flex-1` sibling — so
    // EditPanel's usual right-dock (a flex-row trick every ToolFrame/
    // HybridToolShell consumer relies on, see EditPanel's own CSS comment)
    // doesn't apply here and the panel rendered pinned to the left edge
    // instead. This is a shell-global launch, not a tool-scoped one (like
    // the Calculator popup, ADR-021's other exception), so it gets its own
    // fixed, right-docked wrapper rather than requiring a flex sibling.
    <div className="fixed inset-y-0 right-0 z-50 flex">
      <EditPanel
        open={open}
        title="Client Options"
        subtitle={form?.machineName}
        ariaLabel={`Client Options${titleSuffix}`}
        onClose={onClose}
        // IC-601: 50% wider than EditPanel's 520px default — this dialog's
        // two-column field layout (Vault/Signature/Hardware/Cashier Options)
        // is denser than a typical single-record edit panel and was reported
        // as too narrow. IC-615: widened further — even at 780px the Vault
        // Options dropdowns (long labels like "Locked down cash vault for
        // this machine") were clipping their selected value; see
        // `VaultDropdown`'s stacked layout below for the other half of that
        // fix.
        width="860px"
        primaryLabel={isSaving ? 'Saving…' : 'OK'}
        onPrimary={handleOk}
        primaryDisabled={!form || !!loadError || isSaving}
      >
      <div className="max-h-[78vh] overflow-y-auto px-4 py-3">
        {loadError && (
          <div
            role="alert"
            data-testid="client-options-load-error"
            className="mb-3 rounded border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive"
          >
            {loadError}
          </div>
        )}

        {form && !loadError && (
          <>
            {/* Copy from picker */}
            <div className="mb-3 flex items-center gap-2">
                  <label
                    htmlFor="copy-from"
                    className="text-xs font-medium text-muted-foreground whitespace-nowrap"
                  >
                    Copy client options from:
                  </label>
                  <NativeSelect
                    id="copy-from"
                    data-testid="client-options-copy-from"
                    disabled={!has(PERMISSION.copy) || machineList.length === 0}
                    onChange={(e) => {
                      const id = Number(e.target.value);
                      if (id) handleCopyFrom(id);
                      e.currentTarget.value = '';
                    }}
                    size="sm"
                    className="flex-1"
                  >
                    <NativeSelectOption value="">(Not Selected)</NativeSelectOption>
                    {machineList.map((row) => (
                      <NativeSelectOption key={row.id} value={row.id}>
                        {row.machineName}
                        {row.description ? ` — ${row.description}` : ''}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </div>

                {saveError && (
                  <div
                    role="alert"
                    data-testid="client-options-save-error"
                    className="mb-3 rounded border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive"
                  >
                    {saveError}
                  </div>
                )}

                <Tabs.Root defaultValue="main">
                  <Tabs.List className="mb-3 flex border-b border-border" aria-label="Client Options tabs">
                    <Tabs.Trigger
                      value="main"
                      className="border-b-2 border-transparent px-4 py-1.5 text-xs font-medium data-[state=active]:border-primary data-[state=active]:text-primary"
                    >
                      Main
                    </Tabs.Trigger>
                    <Tabs.Trigger
                      value="tools"
                      className="border-b-2 border-transparent px-4 py-1.5 text-xs font-medium data-[state=active]:border-primary data-[state=active]:text-primary"
                    >
                      Tools
                    </Tabs.Trigger>
                  </Tabs.List>

                  <Tabs.Content value="main" className="grid grid-cols-2 gap-3">
                    {/* Vault Options */}
                    <fieldset className="rounded border border-border p-3">
                      <legend className="px-1 text-xs font-semibold text-muted-foreground">
                        Vault Options
                      </legend>
                      <Checkbox
                        label="Allow vault transactions on this machine"
                        checked={form.allowVaultTransactions}
                        disabled={!has(PERMISSION.allowVault)}
                        onChange={(v) => update('allowVaultTransactions', v)}
                        testId="co-allow-vault"
                      />
                      <Checkbox
                        label="Use original vault for pending transactions"
                        checked={form.useOriginalVaultWhenApprovingPendingTransactions}
                        disabled={!has(PERMISSION.useOriginalVault)}
                        onChange={(v) =>
                          update('useOriginalVaultWhenApprovingPendingTransactions', v)
                        }
                        testId="co-use-original-vault"
                      />
                      <Checkbox
                        label="Require currency bag validation on vault close"
                        checked={form.requireManualCurrencyBagVerificationOnVaultClose}
                        disabled={!has(PERMISSION.cbValidate)}
                        onChange={(v) =>
                          update('requireManualCurrencyBagVerificationOnVaultClose', v)
                        }
                        testId="co-cb-validate"
                      />
                      <Checkbox
                        label="Default tip transactions to tip box instead of vault"
                        checked={form.defaultTipDropToTipBox}
                        disabled={
                          !has(PERMISSION.tipBox) || !form.allowTipBoxTransactions
                        }
                        onChange={(v) => update('defaultTipDropToTipBox', v)}
                        testId="co-tip-box"
                      />
                      <Checkbox
                        label="Warn if transactions are in an open vault"
                        checked={form.warnForTransactionsInOpenVault}
                        disabled={!has(PERMISSION.warnOpenVault)}
                        onChange={(v) => update('warnForTransactionsInOpenVault', v)}
                        testId="co-warn-open-vault"
                      />
                      <Checkbox
                        label="Is mobile vault"
                        checked={form.isMobile}
                        disabled={!has(PERMISSION.isMobile)}
                        onChange={(v) => update('isMobile', v)}
                        testId="co-is-mobile"
                      />
                      <VaultDropdown
                        label="Funding vault for this machine"
                        value={form.fundingVaultTypeId}
                        options={vaultOptions}
                        disabled={!has(PERMISSION.fundingVault)}
                        onChange={(v) => update('fundingVaultTypeId', v)}
                        testId="co-funding-vault"
                      />
                      <VaultDropdown
                        label="Locked down cash vault for this machine"
                        value={form.lockedCashVaultTypeId}
                        options={vaultOptions}
                        disabled={!has(PERMISSION.cashVault)}
                        onChange={(v) => update('lockedCashVaultTypeId', v)}
                        testId="co-cash-vault"
                      />
                      <p className="mt-2 text-[11px] italic text-muted-foreground">
                        The below are for count transfers or CI table/fill/credit/inventory only:
                      </p>
                      <VaultDropdown
                        label="Locked down chip vault for this machine"
                        value={form.lockedChipVaultTypeId}
                        options={vaultOptions}
                        disabled={!has(PERMISSION.chipVault)}
                        onChange={(v) => update('lockedChipVaultTypeId', v)}
                        testId="co-chip-vault"
                      />
                      <VaultDropdown
                        label="Locked down NCV vault for this machine"
                        value={form.lockedPromoChipVaultTypeId}
                        options={vaultOptions}
                        disabled={!has(PERMISSION.ncvChipVault)}
                        onChange={(v) => update('lockedPromoChipVaultTypeId', v)}
                        testId="co-ncv-vault"
                      />
                    </fieldset>

                    {/* Right column: Signature/Document + Hardware */}
                    <div className="flex flex-col gap-3">
                      <fieldset className="rounded border border-border p-3">
                        <legend className="px-1 text-xs font-semibold text-muted-foreground">
                          Signature / Document Options
                        </legend>
                        <div className="mb-2 grid grid-cols-[auto_1fr] items-center gap-2">
                          <label
                            htmlFor="co-sig-type"
                            className="text-xs text-muted-foreground"
                          >
                            Digital signature type:
                          </label>
                          <NativeSelect
                            id="co-sig-type"
                            data-testid="co-sig-type"
                            value={form.digitalSignatureType}
                            disabled={!has(PERMISSION.sigType) || !form.useDigitalSignatures}
                            onChange={(e) =>
                              update(
                                'digitalSignatureType',
                                e.target.value as DigitalSignatureType,
                              )
                            }
                            size="sm"
                            className="w-full"
                          >
                            {SIGNATURE_TYPE_OPTIONS.map((opt) => (
                              <NativeSelectOption key={opt.value} value={opt.value}>
                                {opt.label}
                              </NativeSelectOption>
                            ))}
                          </NativeSelect>
                        </div>
                        <Checkbox
                          label="Signature pad: show summary page before signature"
                          checked={form.signaturePadShowSummaryPage}
                          disabled={!has(PERMISSION.sigSummary)}
                          onChange={(v) => update('signaturePadShowSummaryPage', v)}
                          testId="co-sig-summary"
                        />
                        <Checkbox
                          label="Display all documents after transactions"
                          checked={form.displayAllDocuments}
                          disabled={!has(PERMISSION.autoForms)}
                          onChange={(v) => update('displayAllDocuments', v)}
                          testId="co-display-all-docs"
                        />
                      </fieldset>

                      <fieldset className="rounded border border-border p-3">
                        <legend className="px-1 text-xs font-semibold text-muted-foreground">
                          Hardware Options
                        </legend>
                        <TextField
                          label="Machine description:"
                          value={form.description}
                          maxLength={3000}
                          onChange={(v) => update('description', v)}
                          testId="co-description"
                        />
                        <TextField
                          label="Full size printer:"
                          value={form.fullSizePrinterName}
                          maxLength={1000}
                          onChange={(v) => update('fullSizePrinterName', v)}
                          testId="co-full-printer"
                        />
                        <TextField
                          label="Receipt size printer:"
                          value={form.receiptSizePrinterName}
                          maxLength={1000}
                          onChange={(v) => update('receiptSizePrinterName', v)}
                          testId="co-receipt-printer"
                        />
                        <TextField
                          label="Check printer:"
                          value={form.checkPrinterName}
                          maxLength={1000}
                          onChange={(v) => update('checkPrinterName', v)}
                          testId="co-check-printer"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <TextField
                            label="Barcode prefix:"
                            value={form.barcodePrefix}
                            maxLength={10}
                            onChange={(v) => update('barcodePrefix', v)}
                            testId="co-barcode-prefix"
                          />
                          <TextField
                            label="Barcode suffix:"
                            value={form.barcodeSuffix}
                            maxLength={10}
                            onChange={(v) => update('barcodeSuffix', v)}
                            testId="co-barcode-suffix"
                          />
                        </div>
                        <TextField
                          label="Currency counter provider:"
                          value={form.currencyCounterProvider}
                          maxLength={100}
                          onChange={(v) => update('currencyCounterProvider', v)}
                          testId="co-counter-provider"
                        />
                        <TextField
                          label="Currency counter IP address:"
                          value={form.currencyCounterIp}
                          maxLength={100}
                          // D18 — soft IPv4 hint, blank allowed.
                          pattern="^$|^(\d{1,3}\.){3}\d{1,3}$"
                          onChange={(v) => update('currencyCounterIp', v)}
                          testId="co-counter-ip"
                        />
                      </fieldset>

                      <fieldset className="rounded border border-border p-3">
                        <legend className="px-1 text-xs font-semibold text-muted-foreground">
                          Cashier Options
                        </legend>
                        <Checkbox
                          label="Show cashier variance during close shift / even exchange"
                          checked={form.showCashierVariance}
                          disabled={!has(PERMISSION.showVariance)}
                          onChange={(v) => update('showCashierVariance', v)}
                          testId="co-show-variance"
                        />
                        <Checkbox
                          label="Show other cashiers' shifts"
                          checked={form.showOtherCashierShifts}
                          disabled={!has(PERMISSION.showOtherShifts)}
                          onChange={(v) => update('showOtherCashierShifts', v)}
                          testId="co-show-other-shifts"
                        />
                        <Checkbox
                          label="Remind cashier to enter prior day's transactions and roll shift"
                          checked={form.remindCashiersToRollShift}
                          disabled={!has(PERMISSION.remindRoll)}
                          onChange={(v) => update('remindCashiersToRollShift', v)}
                          testId="co-remind-roll"
                        />
                      </fieldset>
                    </div>
                  </Tabs.Content>

                  <Tabs.Content value="tools" className="space-y-3">
                    <fieldset className="rounded border border-border p-3">
                      <legend className="px-1 text-xs font-semibold text-muted-foreground">
                        Comparison Tool
                      </legend>
                      <p className="mb-2 text-[11px] text-muted-foreground">
                        v1 ships these as freeform paths/arguments. Browse, install
                        links, and default-args dropdown shipped in a future sprint.
                      </p>
                      <TextField
                        label="Comparison tool path:"
                        value={form.comparisonToolFilePath}
                        maxLength={1000}
                        onChange={(v) => update('comparisonToolFilePath', v)}
                        testId="co-tool-path"
                      />
                      <TextField
                        label="Text comparison arguments:"
                        value={form.comparisonToolTextComparisonArguments}
                        maxLength={1000}
                        onChange={(v) =>
                          update('comparisonToolTextComparisonArguments', v)
                        }
                        testId="co-tool-text-args"
                      />
                      <TextField
                        label="Table comparison arguments:"
                        value={form.comparisonToolTableComparisonArguments}
                        maxLength={1000}
                        onChange={(v) =>
                          update('comparisonToolTableComparisonArguments', v)
                        }
                        testId="co-tool-table-args"
                      />
                    </fieldset>
                  </Tabs.Content>
                </Tabs.Root>
              </>
            )}
      </div>
      </EditPanel>
    </div>
  );
}

// ── Small primitives — kept inline since they're dialog-specific ─────────

interface CheckboxProps {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  testId: string;
}
function Checkbox({ label, checked, disabled, onChange, testId }: CheckboxProps) {
  return (
    <label className="mb-1.5 flex items-start gap-2 text-xs">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        className="mt-0.5 h-3.5 w-3.5 rounded border disabled:cursor-not-allowed disabled:opacity-50"
      />
      <span className={disabled ? 'text-muted-foreground/60' : 'text-foreground'}>
        {label}
      </span>
    </label>
  );
}

interface VaultDropdownProps {
  label: string;
  value: string | null;
  options: Array<{ value: string; label: string; inactive?: boolean | null }>;
  disabled?: boolean;
  onChange: (next: string | null) => void;
  testId: string;
}
function VaultDropdown({
  label,
  value,
  options,
  disabled,
  onChange,
  testId,
}: VaultDropdownProps) {
  return (
    // IC-615: stacked (label above, dropdown below) rather than the
    // side-by-side `grid-cols-[auto_1fr]` every other field here uses — these
    // labels are long enough ("Locked down cash vault for this machine")
    // that the inline layout squeezed the select down to where its selected
    // value clipped instead of showing in full.
    <div className="mb-2 flex flex-col gap-1">
      <label htmlFor={testId} className="text-xs text-muted-foreground">
        {label}
      </label>
      <NativeSelect
        id={testId}
        data-testid={testId}
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        size="sm"
        className="w-full"
      >
        <NativeSelectOption value="">(Not Selected)</NativeSelectOption>
        {offeredVaults(options, value).map((opt) => (
          <NativeSelectOption key={opt.value} value={opt.value}>
            {opt.inactive ? `${opt.label} (inactive)` : opt.label}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </div>
  );
}

/**
 * Legacy lists active vault locations only (`VaultTypeLookupCache.GetList` leaves `includeInactive` false). The lookup
 * source still returns inactive rows, so drop them here, except the machine's saved value: it stays shown (and marked)
 * so opening the dialog never silently blanks a saved vault, but nobody can newly pick an inactive one.
 */
function offeredVaults<T extends { value: string; inactive?: boolean | null }>(options: T[], selected: string | null): T[] {
  return options.filter((opt) => !opt.inactive || opt.value === selected);
}

interface TextFieldProps {
  label: string;
  value: string | null;
  maxLength?: number;
  pattern?: string;
  onChange: (next: string | null) => void;
  testId: string;
}
function TextField({
  label,
  value,
  maxLength,
  pattern,
  onChange,
  testId,
}: TextFieldProps) {
  return (
    <div className="mb-1.5 grid grid-cols-[auto_1fr] items-center gap-2">
      <label htmlFor={testId} className="text-xs text-muted-foreground whitespace-nowrap">
        {label}
      </label>
      <input
        id={testId}
        data-testid={testId}
        type="text"
        value={value ?? ''}
        maxLength={maxLength}
        pattern={pattern}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        className="h-7 w-full rounded border bg-background px-2 text-xs"
      />
    </div>
  );
}
